import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Fragment, type HTMLAttributes, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../utils/cn';

const SIZE_WIDTH: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'max-w-sm',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  staticBackdrop?: boolean;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

function ModalRoot({ open, onClose, staticBackdrop = false, size = 'md', children }: ModalProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => (staticBackdrop ? undefined : onClose())}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-2"
            >
              <DialogPanel
                className={cn(
                  'w-full bg-white rounded-md shadow-lg overflow-hidden',
                  SIZE_WIDTH[size],
                )}
              >
                {children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

interface ModalHeaderProps {
  title: ReactNode;
  onClose?: () => void;
}

function ModalHeader({ title, onClose }: ModalHeaderProps) {
  return (
    <div className="flex items-center justify-between border-b border-hilevel-border px-6 py-4">
      <h4 className="text-lg font-semibold text-hilevel-slate">{title}</h4>
      {onClose && (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent p-0 text-hilevel-gray hover:bg-hilevel-light hover:text-hilevel-slate transition cursor-pointer"
        >
          <X className="text-base" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function ModalBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('px-6 py-5 space-y-3 text-sm text-hilevel-slate', className)} {...rest}>
      {children}
    </div>
  );
}

function ModalFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex justify-end gap-2 border-t border-hilevel-border bg-hilevel-offwhite px-6 py-4',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
});
